"use client";

import Link from "next/link";
import { useState } from "react";
import { categories, SidebarCategory } from "./utils";
import { useAppContext } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import {
  CaretDoubleLeftIcon,
  CaretDoubleRightIcon,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { SidebarLogo } from "./SidebarLogo";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { api } from "@/trpc/react";
import OrganizationSelector from "./OrganizationSelector";

export function Sidebar() {
  const { currentProject } = useAppContext();
  const currentProjectId = currentProject?.id;
  const hideLogsFeatureFlag = useFeatureFlagEnabled("hide-logs");
  const enableRemoteEndpointsFlag = useFeatureFlagEnabled(
    "enable-remote-endpoints",
  );

  const [isCollapsed, setIsCollapsed] = useState(false);
  const handleCollapseSidebar = () => setIsCollapsed((prev) => !prev);

  // Check if user has any remote endpoints configured
  const { data: hasRemoteEndpoints, isLoading: isLoadingRemoteEndpoints } =
    api.remoteEndpoints.hasRemoteEndpoints.useQuery(
      { projectId: currentProjectId! },
      { enabled: !!currentProjectId },
    );

  // Get categories and filter based on feature flags and remote endpoints
  const filteredCategories = categories(currentProjectId ?? "").map(
    (category) => {
      if (category.title === "PLATFORM" && hideLogsFeatureFlag) {
        return {
          ...category,
          items: category.items.filter((item) => item.name !== "Logs"),
        };
      }
      // Hide Remote Endpoints menu item if:
      // - Feature flag is not enabled AND user has no existing endpoints
      // Show it if either condition is true:
      // - User has existing endpoints (backward compatibility)
      // - OR feature flag is enabled (new users who need it)
      if (
        category.title === "CURRENT PROJECT" &&
        !enableRemoteEndpointsFlag &&
        (isLoadingRemoteEndpoints || hasRemoteEndpoints !== true)
      ) {
        return {
          ...category,
          items: category.items.filter(
            (item) => item.name !== "Remote Endpoints",
          ),
        };
      }
      return category;
    },
  );

  return (
    <div
      className={cn(
        "group relative flex h-full flex-col gap-spacing-6 bg-palette-surface-default50 rounded-lg border border-palette-border-default transition-all duration-300 ease-in-out",
        isCollapsed ? "w-[70px]" : "w-[262px]",
      )}
    >
      <div className="flex h-[66px] items-center p-spacing-5 gap-2.5">
        <Link href="/dashboard">
          <SidebarLogo isCollapsed={isCollapsed} />
        </Link>
      </div>

      <div
        className={`absolute top-[56px] ${isCollapsed ? "left-[40px]" : "left-[232px]"} bg-palette-surface-main rounded-sm opacity-0 pointer-events-none transition-all duration-300 ease-in-out group-hover:opacity-100 group-hover:pointer-events-auto`}
      >
        <Button
          variant="ghost"
          size="icon"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          onClick={handleCollapseSidebar}
          className={cn(
            `h-8 w-8 flex-none rounded-sm border border-palette-border-default bg-palette-surface-default50 flex items-center justify-center gap-2.5`,
          )}
          style={{
            boxShadow: "0px 6px 6px -2px rgba(1, 5, 7, 0.08)",
          }}
        >
          {isCollapsed ? (
            <CaretDoubleRightIcon size={16} />
          ) : (
            <CaretDoubleLeftIcon size={16} />
          )}
        </Button>
      </div>

      {/* Organization Selector */}
      <div className="px-spacing-3">
        <OrganizationSelector isCollapsed={isCollapsed} />
      </div>

      <nav
        className={`flex flex-col px-spacing-3 ${isCollapsed ? "gap-spacing-2" : "gap-spacing-6"}`}
      >
        {filteredCategories.map((category) => (
          <SidebarCategory
            key={category.title}
            title={category.title}
            items={category.items}
            isCollapsed={isCollapsed}
          />
        ))}
      </nav>

      <div className="flex grow"></div>
    </div>
  );
}
